import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  X,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { debounce } from "lodash";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import Modal from "components/shared/Modal";
import SearchInput from "components/shared/SearchInput";
import { PageControl } from "components/shared/PageControl";
import { Spinner } from "components/shared/Spinner";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Spinner as UiSpinner } from "components/ui/spinner";
import { makeAuthedRequest } from "shared/api/authRequest";
import { TenantResponse } from "api/ApiClient";
import { ApplicationState } from "store";
import * as TenantsState from "store/Tenants";
import { PROPERTY_STATUS_OPTIONS } from "./hooks/usePropertyData";
import type { PropertyStatus } from "./hooks/usePropertyData";
import PhysicalKeySyncSuggestionDialog from "components/shared/PhysicalKeySyncSuggestionDialog";
import {
  type PhysicalKeySyncSuggestion,
  physicalKeySyncSuggestionsOf,
} from "components/shared/physicalKeySyncSuggestions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";

// Empty string represents "no change" -- the corresponding field is omitted from the payload.
type PropertyStatusSelection = PropertyStatus | "";

// The target property is created as Staging when it doesn't already exist, and the API rejects
// Staging -> Archived, so Archived is never a valid initial status for the new property.
const NEW_PROPERTY_STATUS_OPTIONS = PROPERTY_STATUS_OPTIONS.filter((status) => status !== "Archived");

interface PropertyMigratePayload {
  oldPropertyExternalId: string;
  newPropertyExternalId: string;
  newPropertyName: string;
  newTenantId: string | null;
  oldPropertyStatus?: PropertyStatus;
  newPropertyStatus?: PropertyStatus;
}

interface PropertyInfo {
  propertyExternalId: string | null;
  name: string | null;
  physicalKeyExtension: boolean | null;
  status?: PropertyStatus | null;
}

interface TenantInfo {
  id: string;
  name: string | null;
}

interface NamedItem {
  id: string;
  name: string | null;
}

interface PropertyMigrationResponse {
  oldProperty: PropertyInfo;
  newProperty: PropertyInfo;
  oldTenant: TenantInfo;
  newTenant: TenantInfo;
  sitesMigrated: number;
  sites: NamedItem[] | null;
  accessControlGroupsMigrated: number;
  accessControlGroups: NamedItem[] | null;
  accountsMigrated: number;
  accounts: NamedItem[] | null;
  propertyCreated: boolean;
  tenantChanged: boolean;
  /** Sharing Schlage sites whose fobs may need a Fobs sync after the move (newer APIs only). */
  physicalKeySyncSuggestions?: PhysicalKeySyncSuggestion[] | null;
}

interface PropertyMigrateModalProps {
  show: boolean;
  onHide: () => void;
  oldPropertyExternalId: string;
  currentOldPropertyStatus?: PropertyStatus | null;
}

interface InjectedProps {
  tenants: TenantsState.TenantsState;
  setFetchParamsTenants: (params: { page: number; pageSize: number; searchTerm: string }) => void;
  nextTenantsPage: () => void;
  prevTenantsPage: () => void;
}

type Props = PropertyMigrateModalProps & InjectedProps;

const PAGE_SIZE = 5;
const NO_STATUS_CHANGE_VALUE = "__no_status_change__";

export function PropertyMigrateModalInner({
  show,
  onHide,
  oldPropertyExternalId,
  currentOldPropertyStatus,
  tenants: tenantsState,
  setFetchParamsTenants,
  nextTenantsPage,
  prevTenantsPage,
}: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [newPropertyExternalId, setNewPropertyExternalId] = useState("");
  const [newPropertyName, setNewPropertyName] = useState("");
  const [oldPropertyStatus, setOldPropertyStatus] = useState<PropertyStatusSelection>("");
  const [newPropertyStatus, setNewPropertyStatus] = useState<PropertyStatusSelection>("");
  const [moveTenants, setMoveTenants] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantResponse | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [migrationResult, setMigrationResult] = useState<PropertyMigrationResponse | null>(null);
  const [syncSuggestions, setSyncSuggestions] = useState<PhysicalKeySyncSuggestion[]>([]);
  const [accountsExpanded, setAccountsExpanded] = useState(false);
  const [sitesExpanded, setSitesExpanded] = useState(false);
  const [acgExpanded, setAcgExpanded] = useState(false);
  const [acgFilter, setAcgFilter] = useState("");

  const refresh = useCallback(async () => {
    try {
      await setFetchParamsTenants({
        page: 0,
        pageSize: PAGE_SIZE,
        searchTerm: searchTerm,
      });
    } catch (e) {
      console.error("Error requesting tenants", e);
    }
  }, [setFetchParamsTenants, searchTerm]);

  const debouncedSearch = useMemo(() => debounce(refresh, 500), [refresh]);

  useEffect(() => {
    if (show && moveTenants && !selectedTenant) {
      refresh();
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [show, moveTenants, selectedTenant]);

  useEffect(() => {
    if (show) {
      setOldPropertyStatus(currentOldPropertyStatus ?? "");
      setNewPropertyStatus("");
    }
  }, [show, currentOldPropertyStatus]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearch();
  };

  const migrateMutation = useMutation({
    mutationFn: async (payload: PropertyMigratePayload) => {
      return makeAuthedRequest<PropertyMigrationResponse>({
        method: "post",
        url: "/api/Property/migrate",
        data: payload,
      });
    },
    onSuccess: (data) => {
      enqueueSnackbar("Property migrated successfully", { variant: "success" });
      setMigrationResult(data);
      setSyncSuggestions(physicalKeySyncSuggestionsOf(data));
      queryClient.invalidateQueries({ queryKey: ["property-config", oldPropertyExternalId] });
      queryClient.invalidateQueries({ queryKey: ["property-config", data.newProperty.propertyExternalId] });
      queryClient.invalidateQueries({ queryKey: ["properties"] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.message || error?.message || "Failed to migrate property";
      enqueueSnackbar(message, { variant: "error" });
    },
  });

  const handleClose = () => {
    setNewPropertyExternalId("");
    setNewPropertyName("");
    setOldPropertyStatus("");
    setNewPropertyStatus("");
    setMoveTenants(false);
    setSelectedTenant(null);
    setSearchTerm("");
    setMigrationResult(null);
    setSyncSuggestions([]);
    onHide();
  };

  const handleMigrate = () => {
    if (!newPropertyExternalId.trim() || !newPropertyName.trim()) {
      enqueueSnackbar("Please fill in all required fields", { variant: "warning" });
      return;
    }

    if (moveTenants && !selectedTenant) {
      enqueueSnackbar("Please select a tenant", { variant: "warning" });
      return;
    }

    migrateMutation.mutate({
      oldPropertyExternalId,
      newPropertyExternalId: newPropertyExternalId.trim(),
      newPropertyName: newPropertyName.trim(),
      newTenantId: moveTenants && selectedTenant ? selectedTenant.id! : null,
      oldPropertyStatus: oldPropertyStatus || undefined,
      newPropertyStatus: newPropertyStatus || undefined,
    });
  };

  const handleRowClick = (tenant: TenantResponse) => {
    setSelectedTenant(tenant);
  };

  const handleClearTenant = () => {
    setSelectedTenant(null);
  };

  const { tenants, isLoading, page, pageSize } = tenantsState;

  // Results view after successful migration
  if (migrationResult) {
    return (
      <>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="size-5 text-emerald-600" />
              Migration Complete
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="grid gap-6">
            {/* Property Info */}
            <div>
              <div className="mb-1 text-sm font-semibold">
                Property Migration
              </div>
              <div className="rounded-md border bg-card p-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">From</div>
                    <div className="text-sm font-medium">{migrationResult.oldProperty.name}</div>
                    <div className="text-xs text-muted-foreground">{migrationResult.oldProperty.propertyExternalId}</div>
                    {migrationResult.oldProperty.status && (
                      <div className="mt-0.5">
                        <Badge variant="outline">{migrationResult.oldProperty.status}</Badge>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">To</div>
                    <div className="text-sm font-medium">{migrationResult.newProperty.name}</div>
                    <div className="text-xs text-muted-foreground">{migrationResult.newProperty.propertyExternalId}</div>
                    {migrationResult.propertyCreated && (
                      <Badge className="ml-1" variant="success">New</Badge>
                    )}
                    {migrationResult.newProperty.status && (
                      <div className="mt-0.5">
                        <Badge variant="outline">{migrationResult.newProperty.status}</Badge>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tenant Info */}
            {migrationResult.tenantChanged && (
              <div>
                <div className="mb-1 text-sm font-semibold">
                  Tenant Change
                </div>
                <div className="rounded-md border bg-card p-4">
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <div className="text-xs text-muted-foreground">From</div>
                      <div className="text-sm font-medium">{migrationResult.oldTenant.name}</div>
                    </div>
                    <div className="flex-1">
                      <div className="text-xs text-muted-foreground">To</div>
                      <div className="text-sm font-medium">{migrationResult.newTenant.name}</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Summary Stats */}
            <div>
              <div className="mb-1 text-sm font-semibold">
                Items Migrated
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{migrationResult.sitesMigrated} Sites</Badge>
                <Badge variant="outline">
                  {migrationResult.accessControlGroupsMigrated} Access Control Groups
                </Badge>
                <Badge variant="outline">{migrationResult.accountsMigrated} Accounts</Badge>
              </div>
            </div>

            {/* Accounts List */}
            {(migrationResult.accounts?.length ?? 0) > 0 && (
              <div>
                <button
                  className="mb-1 flex w-full items-center justify-between text-left text-sm font-semibold"
                  onClick={() => setAccountsExpanded(!accountsExpanded)}
                  type="button"
                >
                  <span>
                    Accounts ({migrationResult.accounts?.length ?? 0})
                  </span>
                  {accountsExpanded ? <ChevronUp /> : <ChevronDown />}
                </button>
                {accountsExpanded && (
                  <Table
                    className="[&_th]:bg-white [&_th]:text-xs [&_th]:font-semibold [&_td]:whitespace-normal [&_td]:text-xs"
                    containerClassName="max-h-50 overflow-y-auto rounded-md border border-slate-200"
                  >
                      <TableHeader className="sticky top-0 z-10 bg-white">
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>ID</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {migrationResult.accounts?.map((account) => (
                          <TableRow key={account.id}>
                            <TableCell>{account.name}</TableCell>
                            <TableCell className="text-[11px] text-muted-foreground">{account.id}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                )}
              </div>
            )}

            {/* Sites List */}
            {(migrationResult.sites?.length ?? 0) > 0 && (
              <div>
                <button
                  className="mb-1 flex w-full items-center justify-between text-left text-sm font-semibold"
                  onClick={() => setSitesExpanded(!sitesExpanded)}
                  type="button"
                >
                  <span>
                    Sites ({migrationResult.sites?.length ?? 0})
                  </span>
                  {sitesExpanded ? <ChevronUp /> : <ChevronDown />}
                </button>
                {sitesExpanded && (
                  <Table
                    className="[&_th]:bg-white [&_th]:text-xs [&_th]:font-semibold [&_td]:whitespace-normal [&_td]:text-xs"
                    containerClassName="max-h-50 overflow-y-auto rounded-md border border-slate-200"
                  >
                      <TableHeader className="sticky top-0 z-10 bg-white">
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>ID</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {migrationResult.sites?.map((site) => (
                          <TableRow key={site.id}>
                            <TableCell>{site.name}</TableCell>
                            <TableCell className="text-[11px] text-muted-foreground">{site.id}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                )}
              </div>
            )}

            {/* Access Control Groups List */}
            {(migrationResult.accessControlGroups?.length ?? 0) > 0 && (
              <div>
                <button
                  className="mb-1 flex w-full items-center justify-between text-left text-sm font-semibold"
                  onClick={() => setAcgExpanded(!acgExpanded)}
                  type="button"
                >
                  <span>
                    Access Control Groups ({migrationResult.accessControlGroups?.length ?? 0})
                  </span>
                  {acgExpanded ? <ChevronUp /> : <ChevronDown />}
                </button>
                {acgExpanded && (
                  <>
                    {(migrationResult.accessControlGroups?.length ?? 0) > 10 && (
                      <div className="mb-1">
                        <Label className="sr-only" htmlFor="property-migrate-acg-filter">
                          Filter groups
                        </Label>
                        <Input
                          id="property-migrate-acg-filter"
                          placeholder="Filter groups..."
                          value={acgFilter}
                          onChange={(event) => setAcgFilter(event.target.value)}
                          className="text-xs"
                        />
                      </div>
                    )}
                    <Table
                      className="[&_th]:bg-white [&_th]:text-xs [&_th]:font-semibold [&_td]:whitespace-normal [&_td]:text-xs"
                      containerClassName="max-h-[250px] overflow-y-auto rounded-md border border-slate-200"
                    >
                        <TableHeader className="sticky top-0 z-10 bg-white">
                          <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>ID</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {migrationResult.accessControlGroups
                            ?.filter((acg) =>
                              !acgFilter ||
                              acg.name?.toLowerCase().includes(acgFilter.toLowerCase()) ||
                              acg.id.toLowerCase().includes(acgFilter.toLowerCase())
                            )
                            .map((acg) => (
                              <TableRow key={acg.id}>
                                <TableCell>{acg.name}</TableCell>
                                <TableCell className="text-[11px] text-muted-foreground">{acg.id}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                  </>
                )}
                </div>
            )}
            </div>
        </Modal.Body>
        <Modal.Footer>
            <Button type="button" variant="outline" onClick={handleClose}>
            Close
          </Button>
          <Button
              type="button"
            onClick={() => {
              handleClose();
              navigate(`/admin/properties/${migrationResult.newProperty.propertyExternalId}`);
            }}
          >
            View New Property
          </Button>
        </Modal.Footer>
      </Modal>
      {/* Over the results: sharing Schlage sites whose fobs may need a Fobs sync. */}
      <PhysicalKeySyncSuggestionDialog
        suggestions={syncSuggestions}
        onClose={() => setSyncSuggestions([])}
      />
      </>
    );
  }

  // Form view
  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Migrate Property</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-4">
          <div className="grid gap-2">
            <Label htmlFor="property-migrate-external-id">New Property External ID</Label>
            <Input
              id="property-migrate-external-id"
              required
              value={newPropertyExternalId}
              onChange={(event) => setNewPropertyExternalId(event.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="property-migrate-name">New Property Name</Label>
            <Input
              id="property-migrate-name"
              required
              value={newPropertyName}
              onChange={(event) => setNewPropertyName(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="grid flex-1 gap-2">
              <Label htmlFor="property-migrate-old-status">
                Old Property Status (after migration)
              </Label>
              <Select
                value={oldPropertyStatus || NO_STATUS_CHANGE_VALUE}
                onValueChange={(status) =>
                  setOldPropertyStatus(
                    status === NO_STATUS_CHANGE_VALUE
                      ? ""
                      : (status as PropertyStatus),
                  )
                }
              >
                <SelectTrigger id="property-migrate-old-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_STATUS_CHANGE_VALUE}>No change</SelectItem>
                  {PROPERTY_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid flex-1 gap-2">
              <Label htmlFor="property-migrate-new-status">
                New Property Status (after migration)
              </Label>
              <Select
                value={newPropertyStatus || NO_STATUS_CHANGE_VALUE}
                onValueChange={(status) =>
                  setNewPropertyStatus(
                    status === NO_STATUS_CHANGE_VALUE
                      ? ""
                      : (status as PropertyStatus),
                  )
                }
              >
                <SelectTrigger id="property-migrate-new-status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_STATUS_CHANGE_VALUE}>No change</SelectItem>
                  {NEW_PROPERTY_STATUS_OPTIONS.map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={moveTenants}
              id="property-migrate-move-tenants"
              onCheckedChange={(checked) => {
                const isChecked = checked === true;
                setMoveTenants(isChecked);
                if (!isChecked) {
                    setSelectedTenant(null);
                    setSearchTerm("");
                }
              }}
            />
            <Label htmlFor="property-migrate-move-tenants">Move Tenants</Label>
          </div>
          {moveTenants && (
            <div className="mt-1">
              {selectedTenant ? (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {selectedTenant.name ?? selectedTenant.id}
                  </Badge>
                  <Button
                    aria-label="Clear selected tenant"
                    onClick={handleClearTenant}
                    size="icon-xs"
                    type="button"
                    variant="ghost"
                  >
                    <X />
                  </Button>
                </div>
              ) : (
                <>
                  <div className="mb-1 flex justify-end">
                    <div className="w-2/5">
                      <SearchInput
                        placeholder="Search tenants"
                        value={searchTerm}
                        onChange={(e) => handleSearchChange(e.target.value)}
                      />
                    </div>
                  </div>
                  {isLoading && <Spinner />}
                  <Table
                    className="[&_th]:bg-white [&_th]:text-sm [&_th]:font-semibold [&_td]:whitespace-normal [&_td]:text-sm"
                    containerClassName="mb-2 overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm"
                  >
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tenants.map((tenant) => (
                          <TableRow
                            className="cursor-pointer"
                            key={tenant.id}
                            onClick={() => handleRowClick(tenant)}
                          >
                            <TableCell>{tenant.name}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  <PageControl
                    onNext={nextTenantsPage}
                    onPrev={prevTenantsPage}
                    currentPage={page}
                    isLoading={isLoading}
                    disablePrev={page === 0}
                    disableNext={tenants.length < pageSize}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="button" variant="outline" onClick={handleClose} disabled={migrateMutation.isPending}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleMigrate}
          disabled={
            migrateMutation.isPending ||
            !newPropertyExternalId.trim() ||
            !newPropertyName.trim() ||
            (moveTenants && !selectedTenant)
          }
        >
          {migrateMutation.isPending ? (
            <UiSpinner className="size-5" />
          ) : (
            "Migrate"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const PropertyMigrateModal = connect(
  (state: ApplicationState) => ({
    tenants: state.tenants,
  }),
  { ...TenantsState.actionCreators }
)(PropertyMigrateModalInner);

export default PropertyMigrateModal;